import React, { useEffect, useMemo, useState } from "react";
import { FiClock, FiImage, FiLoader, FiZap } from "react-icons/fi";
import { TooltipElement } from "@shared_components/components/Tooltip";
import { HandleAxiosPromise } from "@shared_components/axios/HandleAxiosPromise";
import { useMessage } from "@shared_components/providers/MessageProvider";
import { CountdownTimer } from "teamsite/src/pages/NewAiDesign/components/utils";
import { Link } from "react-router-dom";
import {
    GearStudioDesign,
    POST_GEARSTUDIO_NEW_DESIGN_URL,
    UsageData,
    GearstudioProduct,
    GearstudioProductPanel
} from "@src/types";
import { useGearStudioDesign } from "@src/providers/GearstudioDesignProvider";
import { useGearstudioHistory } from "@src/providers/GearstudioHistoryProvider";
import { GearstudioAuthPanel } from "../components/gearstudioauthpanel";
import { AiDesignStudioWrapper } from "@shared_components/mainsite_pages/aidesign/AiDesignStudio";
import {
    DESIGN_INTERACTION_TOOLTIP,
    STYLE_VARIATION_SUFFIXES,
    formatCreatedAt,
    getTemplatePanels,
    normalizeDesigns,
    pickRandomPrompt,
} from "../components/gearstudiobody.utils";

type GearStudioBodyProps = {
    templates: GearstudioProduct[];
    loadingTemplates: boolean;
    loggedIn: boolean;
    user_uuid: string;
    user_email: string;
    showHistory: boolean;
    setShowHistory: React.Dispatch<React.SetStateAction<boolean>>;
};

const INPUT_CONTROL_CLASSNAME = "gs-input px-3 py-2.5 text-sm";
const TEXTAREA_CLASSNAME = "gs-input min-h-[168px] p-3 text-sm";

export const GearStudioBody = ({ templates, loadingTemplates, loggedIn, user_uuid, user_email, showHistory, setShowHistory }: GearStudioBodyProps) => {
    const { setTextData } = useMessage();
    const { usageData, fetchUsageData } = useGearStudioDesign();
    const { prependHistoryDesigns } = useGearstudioHistory();
    const [currentTemplate, setCurrentTemplate] = useState<GearstudioProduct | null>(null);
    const [prompt, setPrompt] = useState("");
    const [panels, setPanels] = useState<GearstudioProductPanel[]>([]);

    const [generating, setGenerating] = useState(false);
    const [allDesignsWorkspaceVisible, setAllDesignsWorkspaceVisible] = useState(false);
    const [newDesigns, setNewDesigns] = useState<GearStudioDesign[]>([]);
    const [selectedDesign, setSelectedDesign] = useState<GearStudioDesign | null>(null);

    const canGenerate = loggedIn && !!currentTemplate && !!prompt.trim() && !generating;

    const activeDesign = useMemo(() => {
        if (selectedDesign) {
            return selectedDesign;
        }
        if (newDesigns.length > 0) {
            return newDesigns[0];
        }
        return null;
    }, [newDesigns, selectedDesign]);

    const shouldShowWorkspace = !showHistory || allDesignsWorkspaceVisible;

    useEffect(() => {
        if (!loggedIn || !user_uuid || loadingTemplates) {
            return;
        }

        fetchUsageData(user_uuid, { showLoading: false });
        const interval = setInterval(() => {
            fetchUsageData(user_uuid, { showLoading: false });
        }, 60000);

        return () => clearInterval(interval);
    }, [fetchUsageData, loggedIn, user_uuid, loadingTemplates]);

    useEffect(() => {
        if (loadingTemplates || templates.length === 0) {
            return;
        }

        const hasValidCurrentTemplate = currentTemplate
            ? templates.some((template) => template.id === currentTemplate.id)
            : false;

        if (hasValidCurrentTemplate) {
            return;
        }

        const firstTemplate = templates[0];
        setCurrentTemplate(firstTemplate);
        setPanels(getTemplatePanels(firstTemplate));
    }, [loadingTemplates, templates, currentTemplate]);

    useEffect(() => {
        if (showHistory) {
            setAllDesignsWorkspaceVisible(false);
        }
    }, [showHistory]);

    const handleTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextTemplate = templates.find((template) => template.id === parseInt(event.target.value, 10)) || null;
        setCurrentTemplate(nextTemplate);
        setPanels(getTemplatePanels(nextTemplate));
    };

    const handleRandomPrompt = () => {
        setPrompt(pickRandomPrompt());
    };

    const handleGenerate = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!canGenerate || !currentTemplate || !user_uuid) {
            return;
        }

        if (showHistory) {
            setAllDesignsWorkspaceVisible(true);
        }
        setGenerating(true);
        const trimmedPrompt = prompt.trim();

        const payload = {
            template_id: currentTemplate.id,
            prompts: STYLE_VARIATION_SUFFIXES.map((suffix) => `${trimmedPrompt}${suffix}`),
            panels,
            user_data: user_email,
        };

        try {
            const response = await HandleAxiosPromise({
                url: POST_GEARSTUDIO_NEW_DESIGN_URL,
                method: "post",
                data: payload,
                additional_headers: { "User-Id": user_uuid },
            });
            const createdDesigns = normalizeDesigns(response?.data || {});
            if (createdDesigns.length === 0) {
                setTextData({ text: "No designs were generated. Try a different prompt.", variant: "warning" });
                return;
            }

            if (newDesigns.length > 0) {
                prependHistoryDesigns(newDesigns);
            }
            setNewDesigns(createdDesigns);
            setSelectedDesign(createdDesigns[0]);
            setPrompt("");
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to generate designs. Please try again.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setGenerating(false);
            fetchUsageData(user_uuid, { showLoading: false });
        }
    };

    return (
        <div className="grid w-full grid-cols-1 items-start gap-6 lg:grid-cols-[380px_minmax(0,1fr)] xl:gap-8">
            <DesignGeneratorPanel
                loggedIn={loggedIn}
                usageData={usageData}
                currentTemplate={currentTemplate}
                templates={templates}
                loadingTemplates={loadingTemplates}
                prompt={prompt}
                generating={generating}
                canGenerate={canGenerate}
                onTemplateChange={handleTemplateChange}
                onPromptChange={setPrompt}
                onRandomPrompt={handleRandomPrompt}
                onSubmit={handleGenerate}
            />

            <section className="space-y-6">
                {shouldShowWorkspace ? (
                    <WorkspaceSection
                        loggedIn={loggedIn}
                        generating={generating}
                        activeDesign={activeDesign}
                        newDesigns={newDesigns}
                        onSelectDesign={setSelectedDesign}
                    />
                ) : null}

                <HistorySection
                    loggedIn={loggedIn}
                    showHistory={showHistory}
                    setShowHistory={setShowHistory}
                />
            </section>
        </div>
    );
};

type DesignGeneratorPanelProps = {
    loggedIn: boolean;
    usageData: UsageData;
    currentTemplate: GearstudioProduct | null;
    templates: GearstudioProduct[];
    loadingTemplates: boolean;
    prompt: string;
    generating: boolean;
    canGenerate: boolean;
    onTemplateChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    onPromptChange: (nextPrompt: string) => void;
    onRandomPrompt: () => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

const DesignGeneratorPanel = ({
    loggedIn,
    usageData,
    currentTemplate,
    templates,
    loadingTemplates,
    prompt,
    generating,
    canGenerate,
    onTemplateChange,
    onPromptChange,
    onRandomPrompt,
    onSubmit,
}: DesignGeneratorPanelProps) => (
    <aside className="w-full lg:sticky lg:top-6">
        <div className="gs-surface overflow-hidden">
            <div className="space-y-3 p-6">
                <p className="gs-muted font-mono text-[11px] uppercase tracking-[0.16em]">
                    Gear Studio
                </p>
                <h2 className="gs-primary font-serif text-3xl leading-tight">Create a New Design</h2>
                <p className="gs-secondary text-sm">
                    Generate multiple concept designs in one run and pick your best variation.
                </p>

                {loggedIn ? (
                    <div className="mt-4">
                        <CountdownTimer usage={usageData} variant="gearstudio" />
                    </div>
                ) : (
                    <p className="gs-secondary mt-4 rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] px-3 py-2 text-xs">
                        Sign in to load templates and generate designs.
                    </p>
                )}
            </div>

            <form className="gs-divider-top space-y-4 bg-[color:var(--gs-bg-surface-alt)] p-6" onSubmit={onSubmit}>
                <div className="space-y-2">
                    <label htmlFor="template" className="gs-secondary text-sm font-semibold">
                        Choose Product
                    </label>
                    <select
                        id="template"
                        value={currentTemplate?.id || ""}
                        onChange={onTemplateChange}
                        disabled={!loggedIn || loadingTemplates}
                        required
                        className={INPUT_CONTROL_CLASSNAME}
                    >
                        <option value="">
                            {!loggedIn ? "Sign in to load templates" : loadingTemplates ? "Loading templates..." : "Select a product..."}
                        </option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <label htmlFor="user_prompt" className="gs-secondary text-sm font-semibold">
                                Design Prompt
                            </label>
                            <TooltipElement text="Describe the style, mood, colors, and visual direction you want." />
                        </div>
                        <button
                            type="button"
                            onClick={onRandomPrompt}
                            disabled={!loggedIn}
                            className="gs-btn-primary px-3 py-1 text-xs font-semibold"
                        >
                            Random Prompt
                        </button>
                    </div>

                    <textarea
                        id="user_prompt"
                        value={prompt}
                        onChange={(event) => onPromptChange(event.target.value)}
                        disabled={!loggedIn}
                        required
                        className={TEXTAREA_CLASSNAME}
                        placeholder="Example: aggressive matte black base with neon orange lightning graphics and subtle carbon texture"
                    />
                    <p className="gs-muted text-xs">
                        Tip: include palette, finish, and mood for stronger outputs.
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={!canGenerate}
                    className="gs-btn-primary w-full px-4 py-3 text-sm"
                >
                    {generating ? (
                        <span className="flex items-center justify-center gap-2">
                            <FiLoader className="h-5 w-5 animate-spin" aria-hidden="true" />
                            Generating...
                        </span>
                    ) : (
                        "Generate Designs"
                    )}
                </button>
            </form>
        </div>
    </aside>
);

type WorkspaceSectionProps = {
    loggedIn: boolean;
    generating: boolean;
    activeDesign: GearStudioDesign | null;
    newDesigns: GearStudioDesign[];
    onSelectDesign: (design: GearStudioDesign) => void;
};

const WorkspaceSection = ({
    loggedIn,
    generating,
    activeDesign,
    newDesigns,
    onSelectDesign,
}: WorkspaceSectionProps) => (
    <div className="gs-surface space-y-6 p-6 sm:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <p className="gs-muted font-mono text-[11px] uppercase tracking-[0.14em]">
                    Workspace
                </p>
            </div>
        </div>

        {generating ? (
            <div className="gs-surface-alt flex min-h-[420px] flex-col items-center justify-center px-6 text-center">
                <div className="rounded-full bg-[color:rgba(201,107,59,0.14)] p-4">
                    <FiLoader className="h-10 w-10 animate-spin text-[var(--gs-accent-primary)]" aria-hidden="true" />
                </div>
                <h4 className="gs-primary mt-4 text-xl font-semibold">Generating designs...</h4>
            </div>
        ) : activeDesign ? (
            <ActiveWorkspaceDesign
                activeDesign={activeDesign}
                newDesigns={newDesigns}
                onSelectDesign={onSelectDesign}
            />
        ) : (
            <EmptyWorkspace loggedIn={loggedIn} />
        )}
    </div>
);

type ActiveWorkspaceDesignProps = {
    activeDesign: GearStudioDesign;
    newDesigns: GearStudioDesign[];
    onSelectDesign: (design: GearStudioDesign) => void;
};

const ActiveWorkspaceDesign = ({
    activeDesign,
    newDesigns,
    onSelectDesign,
}: ActiveWorkspaceDesignProps) => (
    <div className="space-y-4">
        <div className="gs-surface-alt p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h4 className="gs-primary text-2xl font-semibold">
                        {activeDesign.name || "Generated Design"}
                    </h4>
                    {activeDesign.user_prompt ? (
                        <p className="gs-secondary mt-3 rounded-[var(--gs-radius-input)] bg-[color:var(--gs-input-bg)] px-3 py-2 text-sm">
                            {activeDesign.user_prompt}
                        </p>
                    ) : null}
                </div>
                <p className="gs-muted font-mono text-[11px] uppercase tracking-[0.12em]">
                    {activeDesign.created_at ? `Created ${formatCreatedAt(activeDesign.created_at)}` : ""}
                </p>
            </div>
        </div>

        {newDesigns.length > 1 ? (
            <VariationSelector
                designs={newDesigns}
                selectedDesignId={activeDesign.id}
                onSelectDesign={onSelectDesign}
            />
        ) : null}

        {activeDesign.product_template ? (
            <DesignCard design={activeDesign} isNew={true} />
        ) : (
            <div className="rounded-[var(--gs-radius-input)] border border-[color:rgba(201,107,59,0.45)] bg-[color:rgba(201,107,59,0.12)] p-4 text-sm text-[var(--gs-text-primary)]">
                Missing 3D model data for this design. Cannot display preview.
            </div>
        )}
    </div>
);

type VariationSelectorProps = {
    designs: GearStudioDesign[];
    selectedDesignId: number;
    onSelectDesign: (design: GearStudioDesign) => void;
};

const VariationSelector = ({
    designs,
    selectedDesignId,
    onSelectDesign,
}: VariationSelectorProps) => (
    <div className="space-y-1.5">
        <p className="gs-muted font-mono text-[11px] uppercase tracking-[0.14em]">
            Choose Variation
        </p>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2 xl:grid-cols-4">
            {designs.map((design, index) => {
                const productName = design.name + " " + (index + 1) || `Variation ${index + 1}`;
                const isSelected = selectedDesignId === design.id;
                return (
                    <div
                        key={design.id}
                        className={`gs-variation-btn px-2 py-1.5 text-left sm:px-3 sm:py-2 ${isSelected ? "gs-variation-btn-active" : ""}`}
                    >
                        <button
                            type="button"
                            onClick={() => onSelectDesign(design)}
                            className="flex w-full items-center justify-between gap-2 text-left"
                        >
                            <p className="truncate text-xs font-semibold sm:text-sm">
                                {productName}
                            </p>
                            <p className="gs-muted hidden shrink-0 text-[11px] sm:block">
                                {formatCreatedAt(design.created_at, false) || "Just now"}
                            </p>
                        </button>   
                    </div>
                );
            })}
        </div>
    </div>
);

type EmptyWorkspaceProps = {
    loggedIn: boolean;
};

const EmptyWorkspace = ({ loggedIn }: EmptyWorkspaceProps) => (
    <div className={`flex min-h-[340px] flex-col items-center gap-6 rounded-[var(--gs-radius-card)] border border-dashed border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] p-6 text-center ${loggedIn ? "justify-center" : "justify-start"}`}>
        {!loggedIn ? (
            <div className="w-full max-w-lg">
                <GearstudioAuthPanel formOnly />
            </div>
        ) : (
            <div className="flex max-w-md flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--gs-input-bg)]">
                    <FiImage className="h-8 w-8 text-[var(--gs-text-secondary)]" aria-hidden="true" />
                </div>
                <h4 className="gs-primary text-xl font-semibold">Ready to create</h4>
                <p className="gs-secondary mt-2 text-sm">
                    Pick a product, describe the style, and generate your first concept set.
                </p>
            </div>
        )}
    </div>
);

type HistorySectionProps = {
    loggedIn: boolean;
    showHistory: boolean;
    setShowHistory: React.Dispatch<React.SetStateAction<boolean>>;
};

const HistorySection = ({
    loggedIn,
    showHistory,
    setShowHistory
}: HistorySectionProps) => {
    const {
        historyDesigns,
        loadingHistory,
        hasMoreHistory,
        loadMoreHistory,
    } = useGearstudioHistory();

    if (!loggedIn) {
        return null;
    }
    return (
        <div className="gs-surface-alt p-5 sm:p-6">

            <div className="space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h4 className="gs-primary font-serif text-2xl">Previous Designs</h4>
                    <button
                        type="button"
                        onClick={() => {
                            setShowHistory((prev) => !prev);
                        }} className="gs-muted text-sm font-semibold underline decoration-[color:var(--gs-border-subtle)] underline-offset-4 transition-colors hover:text-[var(--gs-text-secondary)]"
                    >
                        {showHistory ? "Hide History" : "View History"}
                    </button>
                </div>

                {showHistory && (
                    <div>
                        {historyDesigns.length === 0 ? (
                            <p className="gs-muted rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-input-bg)] px-4 py-3 text-sm">
                                {loadingHistory ? "Loading previous entries..." : "No previous designs found."}
                            </p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {historyDesigns.map((design) => {
                                    return (
                                        <DesignCard key={design.id} design={design} isNew={false} />
                                    )
                                })}
                            </div>
                        )}
                        {hasMoreHistory ? (
                            <div className="flex justify-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => loadMoreHistory()}
                                    disabled={loadingHistory}
                                    className="gs-outline-btn px-5 py-2 text-sm font-semibold"
                                >
                                    {loadingHistory ? "Loading..." : "Load More"}
                                </button>
                            </div>
                        ) : null}
                    </div>
                )}



            </div>

        </div>
    )
}

type DesignCardProps = {
    design: GearStudioDesign;
    isNew: boolean;
};

const DesignCard = ({ design, isNew }: DesignCardProps) => {

    if (!design.product_template?.gltf_file) {
        return (
            <div className="rounded-[var(--gs-radius-input)] border border-[color:rgba(201,107,59,0.45)] bg-[color:rgba(201,107,59,0.12)] p-4 text-sm text-[var(--gs-text-primary)]">
                Missing 3D model data for this design. Cannot display preview.
            </div>
        );
    }


    return (
        <article className="overflow-hidden rounded-[var(--gs-radius-card)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface)] transition-shadow hover:shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
            <div className="flex flex-col gap-2 border-b border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex items-center gap-2">
                    <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: isNew ? "var(--gs-accent-primary)" : "var(--gs-text-muted)" }}
                    />
                    <span className="gs-secondary font-mono text-[11px] uppercase tracking-[0.14em]">
                        {isNew ? "New" : "History"}
                    </span>
                    <TooltipElement text={DESIGN_INTERACTION_TOOLTIP} />
                </div>
                <div className="flex items-center gap-3">
                    <span className="gs-muted text-xs">{formatCreatedAt(design.created_at, false)}</span>
                    <Link
                        to={`/designs/${design.id}/edit/`}
                        className="gs-btn-primary  px-3 py-1 text-[11px] font-semibold "
                    >
                        Edit Design
                    </Link>
                </div>
            </div>
            {design.user_prompt ? (
                <p className="gs-muted border-b border-[color:var(--gs-border-subtle)] px-4 py-2 text-xs">{design.user_prompt}</p>
            ) : null}
            <div className="h-[360px] bg-[color:var(--gs-bg-surface-alt)] sm:h-[460px] lg:h-[520px]">
                <AiDesignStudioWrapper
                    gltf_file={design.product_template as any}
                    panels={design.panels}
                    preserve_drawing_buffer={false}
                />
            </div>
        </article>
    );
};
